import { detectDayRow } from './layout';
import type { Layout } from './layout';
import type { ColumnOverride } from './types';
import type { PlausibilityFlag } from './plausibilityValidator';
import type { AMIParseResult } from './parser';
import { coherenceOf } from './readingSearch';

/**
 * Reading instructions from outside the parser: a saved layout from the library,
 * or one Claude proposed for a file nobody had seen.
 *
 * "AI selects, code computes" (parser.md LOCKED #1). Claude sees a de-identified
 * sample — the header row and the first rows, with anything that could name a
 * customer replaced — and answers with which columns hold what. Nothing else in
 * its answer is used. Code validates the answer, turns it into a Layout and a
 * column choice, and readSheet reads the file with them under every invariant
 * and plausibility check. No number in a report comes from the model.
 *
 * A layout joins the library once its reading is confirmed — by the parser's
 * own reading agreeing with Claude's, or by the preparer confirming — and every
 * later file with the same header reads from the library with no AI call. A
 * library layout whose reading is refused or corrected leaves the library.
 */

/** What the library stores and what a proposal becomes once validated. */
export interface ReadingInstructions {
  layout: Layout;
  columns: ColumnOverride;
}

/** The sample sent to Claude: rows from the header down, de-identified. */
export interface LayoutSample {
  /** Index of the sample's first row in the sheet. */
  firstRowIndex: number;
  rows: string[][];
  width: number;
  utility: string | null;
}

/** Claude's answer, as the JSON schema in the function constrains it. */
export interface LayoutProposal {
  layout: 'single' | 'date-time' | 'day-row';
  /** Sample row holding the column headers (0 is the sample's first row). */
  headerRow: number;
  timestampColumn: number;
  timeColumn: number | null;
  valueColumn: number | null;
  firstSlotColumn: number | null;
  lastSlotColumn: number | null;
  slotLabelsMark: 'start' | 'end' | null;
  intervalMinutes: number | null;
  unit: 'kW' | 'kWh' | 'kVA';
  keepRowsColumn: number | null;
  keepRowsMatching: string | null;
  description: string;
  confident: boolean;
}

const SAMPLE_ROWS = 40;
const MAX_CELL_CHARS = 40;
const EMAIL_RE = /[^\s@]+@[^\s@]+\.[a-z]{2,}/i;
const PHONE_RE = /\(?\b\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/;
const STREET_RE = /\b\d{1,6}\s+([A-Za-z0-9.'-]+\s){0,4}(st|street|ave|avenue|rd|road|dr|drive|ln|lane|blvd|way|ct|court|pl|place|pkwy|parkway|hwy|ter|cir)\b\.?/i;
const LONG_NUMBER_RE = /^\D*\d{7,}\D*$/;

// ─── Layout key ──────────────────────────────────────────────────────────────

/**
 * The library key for a file: SHA-256 over its header row, normalized. Two
 * exports from the same utility system share it; a utility adding or renaming a
 * column produces a new key, and the new layout is learned afresh.
 */
export async function layoutKeyOf(headerRow: (string | null)[] | null | undefined): Promise<string | null> {
  if (!headerRow || headerRow.every(h => !h || !h.trim())) return null;
  const text = headerRow.map(h => (h ?? '').trim().toLowerCase().replace(/\s+/g, ' ')).join('|');
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(digest)].map(b => b.toString(16).padStart(2, '0')).join('');
}

// ─── De-identified sample ────────────────────────────────────────────────────

function cellText(cell: unknown): string {
  if (cell === null || cell === undefined) return '';
  if (cell instanceof Date) return isNaN(cell.getTime()) ? '' : cell.toISOString().replace('.000Z', 'Z');
  return String(cell).trim();
}

/**
 * The first rows of the sheet from the header down, with anything that could
 * name a customer replaced: a column holding one text value on every data row
 * (a name, an address, an account) becomes "[same text on every row]"; long
 * digit runs, email addresses, phone numbers and street addresses become
 * "[removed]". Dates, times, readings, units and short labels pass through —
 * they are what Claude needs to see the layout. Sheet names are never sent.
 */
export function buildLayoutSample(
  rows: unknown[][],
  firstRowIndex: number,
  utility: string | null
): LayoutSample {
  const raw = rows.slice(firstRowIndex, firstRowIndex + SAMPLE_ROWS).map(r => (r ?? []).map(cellText));
  const width = Math.max(0, ...raw.map(r => r.length));
  const data = raw.slice(1);
  const constantText = new Set<number>();
  for (let c = 0; c < width; c++) {
    const values = new Set(data.map(r => r[c] ?? '').filter(v => v !== ''));
    if (values.size === 1 && data.length >= 3) {
      const only = [...values][0];
      if (!/^-?[\d.,]+$/.test(only) && !/^(kwh?|kva|kvar|wh)$/i.test(only)) constantText.add(c);
    }
  }
  const scrub = (v: string, r: number, c: number): string => {
    if (v === '') return '';
    if (r > 0 && constantText.has(c)) return '[same text on every row]';
    if (LONG_NUMBER_RE.test(v) || EMAIL_RE.test(v) || PHONE_RE.test(v) || STREET_RE.test(v)) return '[removed]';
    return v.length > MAX_CELL_CHARS ? `${v.slice(0, MAX_CELL_CHARS)}…` : v;
  };
  return {
    firstRowIndex,
    rows: raw.map((row, r) => Array.from({ length: width }, (_, c) => scrub(row[c] ?? '', r, c))),
    width,
    utility,
  };
}

// ─── Proposal → instructions ─────────────────────────────────────────────────

const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Validate a proposal against its sample and turn it into reading instructions,
 * or null when it does not describe the sample: a column out of range, two roles
 * on one column, a layout missing what it needs. Code checks; it never repairs.
 */
export function instructionsFromProposal(p: LayoutProposal, sample: LayoutSample): ReadingInstructions | null {
  const col = (c: number | null): c is number => c !== null && Number.isInteger(c) && c >= 0 && c < sample.width;
  if (!Number.isInteger(p.headerRow) || p.headerRow < 0 || p.headerRow >= sample.rows.length - 1) return null;
  if (!col(p.timestampColumn)) return null;
  if (!['kW', 'kWh', 'kVA'].includes(p.unit)) return null;
  const headerRowIndex = sample.firstRowIndex + p.headerRow;

  const rowFilter = col(p.keepRowsColumn) && p.keepRowsMatching && p.keepRowsMatching.trim()
    ? { column: p.keepRowsColumn, keep: escapeRegex(p.keepRowsMatching.trim()) }
    : null;

  if (p.layout === 'day-row') {
    if (!col(p.firstSlotColumn) || !col(p.lastSlotColumn) || p.lastSlotColumn - p.firstSlotColumn + 1 < 12) return null;
    if (p.timestampColumn >= p.firstSlotColumn && p.timestampColumn <= p.lastSlotColumn) return null;
    const count = p.lastSlotColumn - p.firstSlotColumn + 1;
    // The labels decide the minutes where the reader can read them; otherwise the
    // proposal's interval and start/end marking do.
    const detected = detectDayRow(sample.rows.slice(p.headerRow)).layout;
    const interval = detected?.kind === 'day-row' ? detected.intervalMinutes : p.intervalMinutes;
    if (!interval || interval <= 0 || 1440 % interval !== 0) return null;
    const slots = detected?.kind === 'day-row'
      ? detected.slots.filter(s => s.column >= p.firstSlotColumn! && s.column <= p.lastSlotColumn!)
      : Array.from({ length: count }, (_, i) => ({
          column: p.firstSlotColumn! + i,
          minutes: (p.slotLabelsMark === 'start' ? i : i + 1) * interval,
        }));
    if (slots.length !== count) return null;
    return {
      layout: {
        timestamp: {
          kind: 'day-row', headerRowIndex, dateColumn: p.timestampColumn, slots,
          intervalMinutes: interval, unit: p.unit, unitAssumed: false,
        },
        rowFilter,
      },
      // Positions in the rewritten sheet: timestamp, reading, unit.
      columns: { timestampIndex: 0, demandIndex: 1, unit: p.unit },
    };
  }

  if (!col(p.valueColumn) || p.valueColumn === p.timestampColumn) return null;
  if (p.layout === 'date-time') {
    if (!col(p.timeColumn) || p.timeColumn === p.timestampColumn || p.timeColumn === p.valueColumn) return null;
    return {
      layout: { timestamp: { kind: 'date-time', dateColumn: p.timestampColumn, timeColumn: p.timeColumn }, rowFilter, headerRowIndex },
      columns: { timestampIndex: p.timestampColumn, demandIndex: p.valueColumn, unit: p.unit },
    };
  }
  return {
    layout: { timestamp: { kind: 'single' }, rowFilter, headerRowIndex },
    columns: { timestampIndex: p.timestampColumn, demandIndex: p.valueColumn, unit: p.unit },
  };
}

// ─── Reconciling the two readings ────────────────────────────────────────────

export type Reconciliation =
  /** Both read the file the same way: use the parser's reading, save the layout. */
  | { kind: 'agree'; result: AMIParseResult }
  /** Both read it, differently: keep the parser's reading, ask the preparer to check. */
  | { kind: 'differ'; result: AMIParseResult }
  /** Only Claude's instructions read it: use them, pending the preparer's confirmation. */
  | { kind: 'ai-only'; result: AMIParseResult }
  /** Claude's instructions did not read it: the parser's result stands. */
  | { kind: 'parser-only'; result: AMIParseResult };

const ok = (r: AMIParseResult | null): r is AMIParseResult => !!r && r.errors.length === 0 && r.data.length > 0;
const peak = (r: AMIParseResult) => r.data.reduce((m, p) => Math.max(m, p.kw), -Infinity);
/** Peaks within half a percent are the same reading (start versus end times shift nothing else). */
const samePeak = (a: AMIParseResult, b: AMIParseResult) => Math.abs(peak(a) - peak(b)) <= 0.005 * Math.max(peak(a), peak(b), 1e-9);

function withFlag(result: AMIParseResult, flag: PlausibilityFlag): AMIParseResult {
  const metadata = result.metadata ?? ({} as NonNullable<AMIParseResult['metadata']>);
  return { ...result, metadata: { ...metadata, plausibilityFlags: [...((metadata.plausibilityFlags ?? []) as PlausibilityFlag[]), flag] } };
}

const columnsOf = (r: AMIParseResult) => {
  const t = (r.metadata?.telemetry ?? {}) as { timestamp_column?: string | null; demand_column?: string | null };
  return `${t.timestamp_column ?? '?'} and ${t.demand_column ?? '?'}`;
};

/**
 * Compare the parser's own reading with the reading Claude's instructions gave.
 * The parser's reading wins whenever it loaded, with one exception: a parser
 * reading of daily-or-coarser values against an interval reading that holds
 * together. One value per day cannot show a 15-minute peak; it is what a
 * day-per-row file looks like read as ordinary rows (one column taken for the
 * day's reading), and the column picker cannot describe that layout, so a
 * preparer could not correct it. Otherwise an AI reading never displaces a
 * loaded deterministic one; it can only confirm it or raise a question.
 */
export function reconcile(parser: AMIParseResult, ai: AMIParseResult | null, description: string): Reconciliation {
  if (ok(parser) && ok(ai)) {
    if (samePeak(parser, ai) && parser.data.length === ai.data.length) return { kind: 'agree', result: parser };
    const parserInterval = coherenceOf(parser.data).intervalMinutes ?? 0;
    const aiShape = coherenceOf(ai.data);
    if (parserInterval >= 1440 && aiShape.holds && (aiShape.intervalMinutes ?? 1440) < 1440) {
      return { kind: 'ai-only', result: aiOnlyFlag(ai, description) };
    }
    return {
      kind: 'differ',
      result: withFlag(parser, {
        check: 'rival_readings',
        severity: 'verify',
        message:
          `TrueLoad read this file from ${columnsOf(parser)}. A second reading of its layout, from ` +
          `${columnsOf(ai)}, gives a different result. Check the columns, or choose them yourself.`,
        detail: { used: columnsOf(parser), alternative: columnsOf(ai) },
      }),
    };
  }
  if (ok(ai)) return { kind: 'ai-only', result: aiOnlyFlag(ai, description) };
  return { kind: 'parser-only', result: parser };
}

function aiOnlyFlag(ai: AMIParseResult, description: string): AMIParseResult {
  return withFlag(ai, {
    check: 'ai_read_layout',
    severity: 'verify',
    message:
      `TrueLoad had not seen this file layout before and read it with AI assistance: ${description} ` +
      'Check that the dates and readings look right, then confirm.',
    detail: { columns: columnsOf(ai) },
  });
}
